'use client';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2 } from 'lucide-react';
import { formatInTimeZone } from 'date-fns-tz';
import { formatMoney } from "@/lib/money";
import { FeeBreakdownTable } from '@/app/billing/components/FeeBreakdownTable';

interface BillingPreviewCardProps {
  preview: any;
  isLoading: boolean;
  isFetching: boolean;
  periodStart?: Date;
  periodEnd?: Date;
}

const formatUTC = (date: Date | string, formatStr: string) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return formatInTimeZone(dateObj, 'UTC', formatStr);
};

export function BillingPreviewCard({
  preview,
  isLoading,
  isFetching,
  periodStart,
  periodEnd,
}: BillingPreviewCardProps) {
  return (
    <Card className="relative">
      <CardHeader>
        {/* Loading Overlay */}
        {(isLoading || isFetching) && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center rounded-lg z-10">
            <div className="flex items-center gap-2 text-sm">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Loading preview...</span>
            </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row justify-between gap-6">
          <div className="flex-1">
            <CardTitle className="text-base font-semibold mb-2">
              Billing Period Preview (UTC)
            </CardTitle>
            {preview ? (
              <>
                <p className="text-xl font-bold">
                  {formatUTC(preview.periodStart, 'MMMM d')} - {formatUTC(preview.periodEnd, 'MMMM d, yyyy')}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Preview of what will be billed for this period
                </p>
              </>
            ) : periodStart && periodEnd ? (
              <>
                <p className="text-xl font-bold">
                  {formatUTC(periodStart, 'MMMM d')} - {formatUTC(periodEnd, 'MMMM d, yyyy')}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Select dates and click "Preview" to see billing estimate
                </p>
              </>
            ) : (
              <Skeleton className="h-8 w-64" />
            )}
          </div>
          <div className="flex-1 md:text-right">
            <p className="text-sm font-semibold mb-3">Transactions This Period</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Approved</p>
                <p className="text-lg font-semibold">{preview?.approvedCount || 0}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">SubClub</p>
                <p className="text-lg font-semibold">{preview?.subclubCount || 0}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Declined</p>
                <p className="text-lg font-semibold">{preview?.declinedCount || 0}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Est. Fees</p>
                <p className="text-lg font-semibold text-green-600">
                  {preview ? formatMoney(preview.estimatedFees, 'USD') : '$0.00'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </CardHeader>

      {/* Fee Breakdown Table */}
      {preview?.feesBreakdown && (
        <CardContent>
          <div className="overflow-x-auto">
            <FeeBreakdownTable
              breakdown={preview.feesBreakdown}
              totalFees={preview.estimatedFees}
            />
          </div>
        </CardContent>
      )}
    </Card>
  );
}
